<template>
  <header class="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 shadow-sm">
    <div class="flex items-center justify-between px-6 py-4">
      <!-- Left section -->
      <div class="flex items-center">
        <button @click="$emit('toggle-sidebar')"
          class="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors mr-4">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"></path>
          </svg>
        </button>

        <!-- Search bar avec recherche en temps réel -->
        <div class="relative hidden md:block">
          <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg class="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
            </svg>
          </div>
          <input type="text" placeholder="Rechercher tickets..." v-model="searchQuery" @input="handleSearch"
            class="pl-10 pr-8 py-2 w-64 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent text-gray-700 dark:text-gray-300 placeholder-gray-500 dark:placeholder-gray-400 transition-all duration-300">
          <!-- Indicateur de recherche -->
          <div v-if="searching" class="absolute right-2 top-1/2 transform -translate-y-1/2">
            <svg class="animate-spin h-3 w-3 text-blue-900 dark:text-blue-400" xmlns="http://www.w3.org/2000/svg"
              fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
              </path>
            </svg>
          </div>
          <!-- Bouton effacer -->
          <button v-if="searchQuery && !searching" @click="clearSearch"
            class="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
            <svg class="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <!-- Indicateur de recherche mobile -->
        <div v-if="searchQuery" class="md:hidden ml-2">
          <span class="text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded-full">
            Recherche: "{{ searchQuery }}"
          </span>
        </div>
      </div>

      <!-- Right section -->
      <div class="flex items-center space-x-4">
        <!-- Notifications -->
        <div class="relative" v-if="canSeeNotifications">
          <!-- Nouveau code avec image PNG -->
          <button @click="toggleNotifications"
            class="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors relative group">
            <div class="relative">
              <!-- Image PNG pour les notifications -->
              <img src="/images/notification.png" alt="Notifications"
                class="w-6 h-6 opacity-70 group-hover:opacity-100 transition-opacity" />
            </div>

            <!-- Badge pour notifications non lues - DESIGN AMÉLIORÉ -->
            <span v-if="unreadCount > 0"
              class="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center animate-pulse border-2 border-white dark:border-gray-900 font-semibold shadow-lg">
              {{ unreadCount > 99 ? '99+' : unreadCount }}
            </span>
          </button>

          <!-- DROPDOWN NOTIFICATIONS - DESIGN COMPLÈTEMENT REPENSÉ -->
          <div v-if="notificationsOpen"
            class="absolute right-0 mt-2 w-96 bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 z-50 max-h-[480px] overflow-hidden backdrop-blur-sm bg-white/95 dark:bg-gray-800/95 transform transition-all duration-300 ease-out"
            :class="notificationsOpen ? 'scale-100 opacity-100' : 'scale-95 opacity-0'">
            <!-- Header amélioré -->
            <div
              class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-750 dark:to-gray-800">
              <div class="flex justify-between items-center">
                <div class="flex items-center space-x-3">
                  <div class="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                    <svg class="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor"
                      viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                        d="M15 17h5l-5 5v-5zM8.414 3.586A2 2 0 0110.828 3h2.344a2 2 0 011.414.586L16 5h4a2 2 0 012 2v2a2 2 0 01-.586 1.414l-1.5 1.5a2 2 0 00-.586 1.414V17a2 2 0 01-2 2H6a2 2 0 01-2-2v-4.672a2 2 0 00-.586-1.414l-1.5-1.5A2 2 0 012 7V5a2 2 0 012-2h4l1.414-1.414z" />
                    </svg>
                  </div>
                  <div>
                    <h3 class="text-lg font-bold text-gray-900 dark:text-white">Notifications</h3>
                    <p class="text-sm text-gray-600 dark:text-gray-400">
                      {{ unreadCount > 0 ? `${unreadCount} non lue(s)` : 'Tout est à jour' }}
                    </p>
                  </div>
                </div>
                <button v-if="unreadCount > 0" @click="markAllAsRead"
                  class="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors font-medium px-3 py-1 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20">
                  Tout lire
                </button>
              </div>
            </div>

            <!-- Liste des notifications - DESIGN AMÉLIORÉ -->
            <div class="overflow-y-auto max-h-80 custom-scrollbar">
              <!-- État de chargement -->
              <div v-if="loading" class="p-8 text-center">
                <div
                  class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-blue-400 mx-auto mb-3">
                </div>
                <p class="text-sm text-gray-500 dark:text-gray-400">Chargement des notifications...</p>
              </div>

              <!-- État vide -->
              <div v-else-if="notifications.length === 0" class="p-8 text-center">
                <div
                  class="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg class="w-8 h-8 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor"
                    viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h4 class="text-lg font-semibold text-gray-900 dark:text-white mb-2">Aucune notification</h4>
                <p class="text-sm text-gray-500 dark:text-gray-400">Vous serez notifié des nouveaux tickets et mises à
                  jour</p>
              </div>

              <!-- Liste des notifications -->
              <div v-else class="divide-y divide-gray-100 dark:divide-gray-700">
                <div v-for="notification in notifications" :key="notification.id"
                  @click="openNotification(notification)" :class="[
                    'p-4 cursor-pointer transition-all duration-200 group relative',
                    notification.is_read
                      ? 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-750'
                      : 'bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 border-l-4 border-l-blue-500'
                  ]">
                  <!-- Indicateur non lu -->
                  <div v-if="!notification.is_read" class="absolute left-2 top-1/2 transform -translate-y-1/2">
                    <div class="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                  </div>

                  <div class="flex space-x-3">
                    <!-- Icône du type de notification -->
                    <div :class="[
                      'flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center',
                      notification.type === 'nouveau_ticket'
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                        : 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                    ]">
                      <svg v-if="notification.type === 'nouveau_ticket'" class="w-5 h-5" fill="none"
                        stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                          d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
                      </svg>
                      <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                          d="M4.5 12.75l6 6 9-13.5" />
                      </svg>
                    </div>

                    <!-- Contenu de la notification -->
                    <div class="flex-1 min-w-0">
                      <div class="flex justify-between items-start mb-1">
                        <h4 class="text-sm font-semibold text-gray-900 dark:text-white leading-tight">
                          {{ notification.title }}
                        </h4>
                        <span class="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0 ml-2">
                          {{ notification.formatted_date }}
                        </span>
                      </div>

                      <p class="text-sm text-gray-600 dark:text-gray-300 mb-2 leading-relaxed">
                        {{ notification.message }}
                      </p>

                      <div class="flex justify-between items-center">
                        <div class="flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-400">
                          <span class="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-full">
                            📋 {{ notification.ticket_code }}
                          </span>
                          <span class="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-full">
                            👤 {{ notification.client_name }}
                          </span>
                        </div>

                        <button v-if="!notification.is_read" @click.stop="markAsRead(notification.id)"
                          class="text-xs text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300 transition-colors font-medium px-2 py-1 rounded hover:bg-green-50 dark:hover:bg-green-900/20 opacity-0 group-hover:opacity-100 transition-opacity">
                          Marquer lu
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Footer amélioré -->
            <div v-if="notifications.length > 0"
              class="px-6 py-4 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-750">
              <button @click="viewAllNotifications"
                class="w-full text-center text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors font-medium py-2 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20">
                Voir toutes les notifications
              </button>
            </div>
          </div>
        </div>

        <!-- User menu (inchangé) -->
        <div class="flex items-center space-x-3">
          <div class="relative">
            <button @click="toggleUserMenu"
              class="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors border border-gray-200 dark:border-gray-600">
              <div
                class="h-8 w-8 rounded-full bg-green-700 flex items-center justify-center text-white text-sm font-semibold shadow-sm">
                {{ userInitials }}
              </div>
              <svg class="h-4 w-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor"
                viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
              </svg>
            </button>

            <!-- Dropdown menu -->
            <div v-if="userMenuOpen"
              class="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-10 backdrop-blur-sm bg-white/95 dark:bg-gray-800/95">
              <!-- Header -->
              <div class="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                <span class="text-sm font-semibold text-gray-800 dark:text-gray-200">{{ userName }}</span> <br>
                <span class="text-xs text-gray-500 dark:text-gray-400">{{ userRole }}</span>
              </div>

              <!-- Menu items -->
              <a href="#" @click.prevent="openProfile"
                class="flex items-center px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                <svg class="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                </svg>
                Mon profil
              </a>
              <div class="border-t border-gray-200 dark:border-gray-700 my-2"></div>

              <button @click="logoutUser"
                class="w-full flex items-center px-4 py-3 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                <svg class="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1">
                  </path>
                </svg>
                Déconnexion
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </header>
</template>

<script>
export default {
  name: 'DashboardNavbar',
  props: {
    sidebarCollapsed: Boolean,
    userName: {
      type: String,
      required: true
    },
    userRole: {
      type: String,
      required: true
    },
    userEmail: {
      type: String,
      default: 'utilisateur@example.com'
    }
  },
  data() {
    return {
      userMenuOpen: false,
      notificationsOpen: false,
      isDark: false,
      searchQuery: '',
      searching: false,
      searchTimeout: null,
      notifications: [],
      unreadCount: 0,
      loading: false,
      pollInterval: null,
      error: null,
      notificationsLoadedOnce: false
    };
  },
  computed: {
    userInitials() {
      if (!this.userName) return '??';
      return this.userName
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .substring(0, 2);
    },
    // Vérifie si l'utilisateur peut voir les notifications
    canSeeNotifications() {
      const role = this.userRole.toLowerCase();
      return role.includes('administrateur') ||
        role.includes('responsable') ||
        role.includes('admin');
    }
  },
  mounted() {
  this.isDark = document.documentElement.classList.contains('dark');
  document.addEventListener('click', this.closeAllMenus);

  // ✅ Charger immédiatement les notifications si l'utilisateur a le droit
  if (this.canSeeNotifications) {
    this.loadNotifications();

    // Polling pour les nouvelles notifications toutes les 30 secondes
    this.pollInterval = setInterval(() => {
      this.loadNotifications();
    }, 30000);
  }
},
  beforeUnmount() {
    document.removeEventListener('click', this.closeAllMenus);
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
    if (this.pollInterval) clearInterval(this.pollInterval);
  },

  watch: {
  // Recharger les notifications quand le menu devient visible
  notificationsOpen(newVal) {
    if (newVal && this.canSeeNotifications) {
      this.loadNotifications();
    }
  }
},
  methods: {
    async initializeNotifications() {
      if (this.canSeeNotifications) {
        await this.loadNotifications();
      }
    },
    toggleUserMenu() {
      this.userMenuOpen = !this.userMenuOpen;
      if (this.userMenuOpen) {
        this.notificationsOpen = false;
      }
    },
    toggleNotifications() {
      if (!this.canSeeNotifications) return;

      this.notificationsOpen = !this.notificationsOpen;
      if (this.notificationsOpen) {
        this.userMenuOpen = false;
        this.loadNotifications();
      }
    },
    closeAllMenus(event) {
      if (!this.$el.contains(event.target)) {
        this.userMenuOpen = false;
        this.notificationsOpen = false;
      }
    },

    // Gestion de la recherche en temps réel
    handleSearch() {
  this.searching = true;
  if (this.searchTimeout) {
    clearTimeout(this.searchTimeout);
  }
  this.searchTimeout = setTimeout(() => {
    this.performSearch();
  }, 500);
},
// Dans DashboardNavbar.vue - methods
performSearch() {
  // CORRECTION : Émettre un objet structuré
  this.$emit('search', {
    query: this.searchQuery.trim(),
    isGlobalSearch: true
  });
  setTimeout(() => {
    this.searching = false;
  }, 300);
},

clearSearch() {
  this.searchQuery = '';
  this.searching = false;
  // CORRECTION : Émettre un objet avec query vide
  this.$emit('search', { query: '', isGlobalSearch: true });
  this.$nextTick(() => {
    const searchInput = this.$el.querySelector('input[type="text"]');
    if (searchInput) {
      searchInput.focus();
    }
  });
},

    // Gestion des notifications
    async loadNotifications() {
      if (!this.canSeeNotifications) return;

      try {
        this.loading = true;
        this.error = null;

        const response = await fetch('/api/notifications', {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
          },
          credentials: 'same-origin'
        });

        if (!response.ok) {
          throw new Error(`Erreur HTTP: ${response.status}`);
        }

        const data = await response.json();

        if (data.success) {
          this.notifications = data.notifications;
          this.unreadCount = data.unread_count;
        } else {
          throw new Error(data.message || 'Erreur inconnue');
        }
      } catch (error) {
        this.error = error.message;
        this.notifications = [];
        this.unreadCount = 0;
      } finally {
        this.loading = false;
      }
    },

    async markAsRead(notificationId) {
      try {
        const response = await fetch(`/api/notifications/${notificationId}/read`, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
          },
          credentials: 'same-origin'
        });

        if (response.ok) {
          // Mettre à jour localement
          const notification = this.notifications.find(n => n.id === notificationId);
          if (notification) {
            notification.is_read = true;
            this.unreadCount = Math.max(0, this.unreadCount - 1);
          }
        }
      } catch (error) {
      }
    },

    async markAllAsRead() {
      try {
        const response = await fetch('/api/notifications/read-all', {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
          },
          credentials: 'same-origin'
        });

        if (response.ok) {
          // Mettre à jour localement
          this.notifications.forEach(notification => {
            notification.is_read = true;
          });
          this.unreadCount = 0;

          // Recharger les notifications pour refléter les changements
          this.loadNotifications();
        }
      } catch (error) {
      }
    },

    openNotification(notification) {

      // Émettre l'événement avec le code du ticket
      this.$emit('open-ticket-by-code', notification.ticket_code);

      // Marquer comme lu
      this.markAsRead(notification.id);

      // Fermer le dropdown des notifications
      this.notificationsOpen = false;
    },

    viewAllNotifications() {
      // Émettre un événement pour aller vers la page des notifications
      this.$emit('view-all-notifications');
      this.notificationsOpen = false;
    },

    logoutUser() {
    console.log('🚪 Déconnexion en cours...');
    
    // 🔥 NETTOYAGE IMMÉDIAT ET COMPLET
    this.performCompleteCleanup();
    
    fetch('/logout', {
        method: 'POST',
        headers: {
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
            'Accept': 'application/json',
        },
    })
    .then(response => {
        console.log('✅ Réponse déconnexion:', response.status);
        
        // 🔥 NETTOYAGE SUPPLÉMENTAIRE APRÈS RÉPONSE
        this.performCompleteCleanup();
        
        if (response.ok) {
            // Redirection avec nettoyage forcé
            setTimeout(() => {
                window.location.href = '/';
            }, 100);
        } else {
            console.error('❌ Erreur lors de la déconnexion');
            // Redirection quand même avec nettoyage
            window.location.href = '/';
        }
    })
    .catch(error => {
        console.error('❌ Erreur réseau:', error);
        // Redirection avec nettoyage même en cas d'erreur
        this.performCompleteCleanup();
        window.location.href = '/';
    });
},

// 🔥 NOUVELLE MÉTHODE DE NETTOYAGE COMPLET
performCompleteCleanup() {
    console.log('🧹 NETTOYAGE COMPLET EN COURS...');
    
    // 1. Supprimer toutes les données sensibles du localStorage
    const allLocalStorageKeys = Object.keys(localStorage);
    allLocalStorageKeys.forEach(key => {
        const lowerKey = key.toLowerCase();
        if (lowerKey.includes('user') || 
            lowerKey.includes('role') || 
            lowerKey.includes('permission') ||
            lowerKey.includes('auth') ||
            lowerKey.includes('token') ||
            lowerKey.includes('session') ||
            lowerKey.includes('admin') ||
            lowerKey.includes('vuex') ||
            lowerKey.includes('laravel')) {
            localStorage.removeItem(key);
            console.log('🗑️ Supprimé:', key);
        }
    });
    
    // 2. Vider complètement sessionStorage
    sessionStorage.clear();
    
    // 3. Supprimer les cookies spécifiques
    this.clearAllCookies();
    
    // 4. Option radicale - tout vider (décommentez si nécessaire)
    // localStorage.clear();
    
    console.log('✅ Nettoyage terminé');
},

// 🔥 NETTOYAGE DES COOKIES
clearAllCookies() {
    const cookies = document.cookie.split(";");
    for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i];
        const eqPos = cookie.indexOf("=");
        const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
        
        // Supprimer le cookie pour tous les paths et domains
        document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
        document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=." + window.location.hostname;
        document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=" + window.location.hostname;
    }
},

    openProfile() {
      this.$emit('open-profile');
      this.userMenuOpen = false;
    },
  }
};
</script>

<style scoped>
.bg-gray-750 {
  background-color: #374151;
}

/* Barre de défilement personnalisée */
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 3px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Version somme */
.dark .custom-scrollbar::-webkit-scrollbar-track {
  background: #374151;
}

.dark .custom-scrollbar::-webkit-scrollbar-thumb {
  background: #4b5563;
}

.dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}

/* Animations */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.notification-enter-active {
  animation: fadeIn 0.3s ease-out;
}
</style>